<script setup>
import { useForm } from '@inertiajs/vue3';
import { getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Card from 'primevue/card';
import Message from 'primevue/message';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    coins: Object,
    categories: Array,
    languages: Array,
    countries: Array,
    sellers: Array,
    teams: Array,
    types: Array,
});

const form = useForm({
    url: '',
    category_id: null,
    da: '',
    dr: '',
    traffic: '',
    language_id: null,
    country_id: null,
    link_type: null,

    gambling: false,
    cdb: false,
    cripto: false,
    sponsor: false,
    menu: false,
    banner: false,

    cost: '',
    cost_coin: 'BRL',

    sale: '',
    sale_coin: 'BRL',

    last_posted: '',
    seller_id: null,
    team_id: null,

    obs: '',

    owner_name: '',
    owner_email: '',
    owner_phone: '',
    bank: '',
    pix: '',
    phone: '',
    paypal: '',
    instagram: '',
    facebook: '',

    owner_role: '',
    promo: false,
    restrict: false,
    restrict_detail: '',
    archive_article: false,
    archive_due: '',
    links: null,
    embed: false,
    images: null,
    example_article: '',
    rules: '',
    google_news: false,
    global_account: '',
});

// Options
const linkTypeOptions = [
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' },
];

const coinOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' },
];

const linksOptions = [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null },
];

const imagesOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
];

const submit = () => {
    form.post(route('sites.store'), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site created successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create site',
                life: 5000
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-plus text-2xl"></i>
                            <span>{{ $t('Add New Site') }}</span>
                        </div>
                    </template>

                    <template #content>
                        <form @submit.prevent="submit">
                            <div class="formgrid grid">
                                <!-- URL -->
                                <div class="field col-12">
                                    <label for="url" class="font-semibold">{{ $t('URL') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="url"
                                        v-model="form.url"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.url }"
                                        :placeholder="$t('Enter website URL')"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.url" class="p-error">{{ form.errors.url }}</small>
                                </div>

                                <!-- Category -->
                                <div class="field col-12">
                                    <label for="category" class="font-semibold">{{ $t('Category') }}</label>
                                    <Select
                                        id="category"
                                        v-model="form.category_id"
                                        :options="categories"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Select Category')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.category_id }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.category_id" class="p-error">{{ form.errors.category_id }}</small>
                                </div>

                                <!-- Metrics -->
                                <div class="field col-12 md:col-4">
                                    <label for="da" class="font-semibold">{{ $t('DA') }}</label>
                                    <InputNumber
                                        id="da"
                                        v-model="form.da"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.da }"
                                        :disabled="form.processing"
                                        :minFractionDigits="0"
                                        :maxFractionDigits="2" />
                                    <small v-if="form.errors.da" class="p-error">{{ form.errors.da }}</small>
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="dr" class="font-semibold">{{ $t('DR') }}</label>
                                    <InputNumber
                                        id="dr"
                                        v-model="form.dr"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.dr }"
                                        :disabled="form.processing"
                                        :minFractionDigits="0"
                                        :maxFractionDigits="2" />
                                    <small v-if="form.errors.dr" class="p-error">{{ form.errors.dr }}</small>
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="traffic" class="font-semibold">{{ $t('Traffic') }}</label>
                                    <InputNumber
                                        id="traffic"
                                        v-model="form.traffic"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.traffic }"
                                        :disabled="form.processing"
                                        :minFractionDigits="0"
                                        :maxFractionDigits="2" />
                                    <small v-if="form.errors.traffic" class="p-error">{{ form.errors.traffic }}</small>
                                </div>

                                <!-- Language and Country -->
                                <div class="field col-12 md:col-6">
                                    <label for="language" class="font-semibold">{{ $t('Language') }}</label>
                                    <Select
                                        id="language"
                                        v-model="form.language_id"
                                        :options="languages"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Select Language')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.language_id }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.language_id" class="p-error">{{ form.errors.language_id }}</small>
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="country" class="font-semibold">{{ $t('Country') }}</label>
                                    <Select
                                        id="country"
                                        v-model="form.country_id"
                                        :options="countries"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Select Country')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.country_id }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.country_id" class="p-error">{{ form.errors.country_id }}</small>
                                </div>

                                <!-- Link Type -->
                                <div class="field col-12">
                                    <label for="link_type" class="font-semibold">{{ $t('Link Type') }}</label>
                                    <Select
                                        id="link_type"
                                        v-model="form.link_type"
                                        :options="linkTypeOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        :placeholder="$t('Select Link Type')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.link_type }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.link_type" class="p-error">{{ form.errors.link_type }}</small>
                                </div>

                                <!-- Categories -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Categories') }}</label>
                                    <div class="flex flex-wrap gap-3 mt-2">
                                        <div class="flex align-items-center">
                                            <Checkbox id="gambling" v-model="form.gambling" :binary="true" />
                                            <label for="gambling" class="ml-2">{{ $t('Gambling') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="cdb" v-model="form.cdb" :binary="true" />
                                            <label for="cdb" class="ml-2">{{ $t('CDB') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="cripto" v-model="form.cripto" :binary="true" />
                                            <label for="cripto" class="ml-2">{{ $t('Crypto') }}</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Features -->
                                <div class="field col-12">
                                    <label class="font-semibold">{{ $t('Features') }}</label>
                                    <div class="flex flex-wrap gap-3 mt-2">
                                        <div class="flex align-items-center">
                                            <Checkbox id="sponsor" v-model="form.sponsor" :binary="true" />
                                            <label for="sponsor" class="ml-2">{{ $t('Sponsor') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="menu" v-model="form.menu" :binary="true" />
                                            <label for="menu" class="ml-2">{{ $t('Menu') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="banner" v-model="form.banner" :binary="true" />
                                            <label for="banner" class="ml-2">{{ $t('Banner') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="promo" v-model="form.promo" :binary="true" />
                                            <label for="promo" class="ml-2">{{ $t('Promo') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="google_news" v-model="form.google_news" :binary="true" />
                                            <label for="google_news" class="ml-2">{{ $t('Google News') }}</label>
                                        </div>
                                        <div class="flex align-items-center">
                                            <Checkbox id="embed" v-model="form.embed" :binary="true" />
                                            <label for="embed" class="ml-2">{{ $t('Embed') }}</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Pricing -->
                                <div class="field col-12 md:col-6">
                                    <label for="cost" class="font-semibold">{{ $t('Cost') }}</label>
                                    <InputGroup>
                                        <InputText
                                            id="cost"
                                            v-model="form.cost"
                                            :class="{ 'p-invalid': form.errors.cost }"
                                            :placeholder="$t('Enter cost')"
                                            :disabled="form.processing" />
                                        <Select
                                            v-model="form.cost_coin"
                                            :options="coinOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            style="width: 80px" />
                                    </InputGroup>
                                    <small v-if="form.errors.cost" class="p-error">{{ form.errors.cost }}</small>
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="sale" class="font-semibold">{{ $t('Sale Price') }}</label>
                                    <InputGroup>
                                        <InputText
                                            id="sale"
                                            v-model="form.sale"
                                            :class="{ 'p-invalid': form.errors.sale }"
                                            :placeholder="$t('Enter sale price')"
                                            :disabled="form.processing" />
                                        <Select
                                            v-model="form.sale_coin"
                                            :options="coinOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            style="width: 80px" />
                                    </InputGroup>
                                    <small v-if="form.errors.sale" class="p-error">{{ form.errors.sale }}</small>
                                </div>

                                <!-- Restrictions -->
                                <div class="field col-12">
                                    <div class="flex align-items-center mb-2">
                                        <Checkbox id="restrict" v-model="form.restrict" :binary="true" />
                                        <label for="restrict" class="ml-2 font-semibold">{{ $t('Restriction') }}</label>
                                    </div>
                                    <InputText
                                        v-if="form.restrict"
                                        id="restrict_detail"
                                        v-model="form.restrict_detail"
                                        class="w-full"
                                        :placeholder="$t('Inform about restrictions')" />
                                </div>

                                <!-- Archive Article -->
                                <div class="field col-12">
                                    <div class="flex align-items-center mb-2">
                                        <Checkbox id="archive_article" v-model="form.archive_article" :binary="true" />
                                        <label for="archive_article" class="ml-2 font-semibold">{{ $t('Archive article') }}</label>
                                    </div>
                                    <InputText
                                        v-if="form.archive_article"
                                        id="archive_due"
                                        v-model="form.archive_due"
                                        class="w-full"
                                        :placeholder="$t('How long')" />
                                </div>

                                <!-- Links and Images -->
                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Number of links in the article') }}</label>
                                    <div class="flex gap-3 mt-2">
                                        <div v-for="option in linksOptions" :key="option.value" class="flex align-items-center">
                                            <input :id="`links_${option.value}`" v-model="form.links" type="radio" :value="option.value" />
                                            <label :for="`links_${option.value}`" class="ml-2">{{ option.label }}</label>
                                        </div>
                                    </div>
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label class="font-semibold">{{ $t('Maximum number of images in content') }}</label>
                                    <div class="flex gap-3 mt-2">
                                        <div v-for="option in imagesOptions" :key="option.value" class="flex align-items-center">
                                            <input :id="`images_${option.value}`" v-model="form.images" type="radio" :value="option.value" />
                                            <label :for="`images_${option.value}`" class="ml-2">{{ option.label }}</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Last Posted -->
                                <div class="field col-12">
                                    <label for="last_posted" class="font-semibold">{{ $t('Last Posted') }}</label>
                                    <InputText
                                        id="last_posted"
                                        v-model="form.last_posted"
                                        type="date"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.last_posted }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.last_posted" class="p-error">{{ form.errors.last_posted }}</small>
                                </div>

                                <!-- Seller and Team -->
                                <div class="field col-12 md:col-6">
                                    <label for="seller" class="font-semibold">{{ $t('Seller') }}</label>
                                    <Select
                                        id="seller"
                                        v-model="form.seller_id"
                                        :options="sellers"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Select Seller')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.seller_id }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.seller_id" class="p-error">{{ form.errors.seller_id }}</small>
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="team" class="font-semibold">{{ $t('Team') }}</label>
                                    <Select
                                        id="team"
                                        v-model="form.team_id"
                                        :options="teams"
                                        optionLabel="name"
                                        optionValue="id"
                                        :placeholder="$t('Select Team')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.team_id }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.team_id" class="p-error">{{ form.errors.team_id }}</small>
                                </div>

                                <!-- Observations -->
                                <div class="field col-12">
                                    <label for="obs" class="font-semibold">{{ $t('Observations') }}</label>
                                    <Textarea
                                        id="obs"
                                        v-model="form.obs"
                                        rows="3"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.obs }" />
                                    <small v-if="form.errors.obs" class="p-error">{{ form.errors.obs }}</small>
                                </div>

                                <!-- Rules -->
                                <div class="field col-12">
                                    <label for="rules" class="font-semibold">{{ $t('Rules for content') }}</label>
                                    <Textarea
                                        id="rules"
                                        v-model="form.rules"
                                        rows="3"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.rules }" />
                                    <small v-if="form.errors.rules" class="p-error">{{ form.errors.rules }}</small>
                                </div>

                                <!-- Example Article -->
                                <div class="field col-12">
                                    <label for="example_article" class="font-semibold">{{ $t('Example article') }}</label>
                                    <InputText
                                        id="example_article"
                                        v-model="form.example_article"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.example_article }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.example_article" class="p-error">{{ form.errors.example_article }}</small>
                                </div>

                                <!-- Owner Info -->
                                <div class="field col-12 md:col-8">
                                    <label for="owner_name" class="font-semibold">{{ $t('Owner name') }}</label>
                                    <InputText
                                        id="owner_name"
                                        v-model="form.owner_name"
                                        class="w-full"
                                        :disabled="form.processing" />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="owner_role" class="font-semibold">{{ $t('Owner role') }}</label>
                                    <InputText
                                        id="owner_role"
                                        v-model="form.owner_role"
                                        class="w-full"
                                        :disabled="form.processing" />
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="owner_email" class="font-semibold">{{ $t('Owner email') }}</label>
                                    <InputText
                                        id="owner_email"
                                        v-model="form.owner_email"
                                        class="w-full"
                                        :disabled="form.processing" />
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="owner_phone" class="font-semibold">{{ $t('Owner phone') }}</label>
                                    <InputText
                                        id="owner_phone"
                                        v-model="form.owner_phone"
                                        class="w-full"
                                        :disabled="form.processing" />
                                </div>

                                <!-- Payment Info -->
                                <div class="field col-12">
                                    <label for="bank" class="font-semibold">{{ $t('Site bank') }}</label>
                                    <Textarea
                                        id="bank"
                                        v-model="form.bank"
                                        rows="3"
                                        class="w-full" />
                                </div>

                                <div class="field col-12">
                                    <label for="global_account" class="font-semibold">{{ $t('Global account') }}</label>
                                    <InputText
                                        id="global_account"
                                        v-model="form.global_account"
                                        class="w-full" />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="pix" class="font-semibold">{{ $t('Site pix') }}</label>
                                    <InputText
                                        id="pix"
                                        v-model="form.pix"
                                        class="w-full" />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="paypal" class="font-semibold">{{ $t('Site paypal') }}</label>
                                    <InputText
                                        id="paypal"
                                        v-model="form.paypal"
                                        class="w-full" />
                                </div>

                                <div class="field col-12 md:col-4">
                                    <label for="phone" class="font-semibold">{{ $t('Site phone') }}</label>
                                    <InputText
                                        id="phone"
                                        v-model="form.phone"
                                        class="w-full" />
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="instagram" class="font-semibold">{{ $t('Site instagram') }}</label>
                                    <InputText
                                        id="instagram"
                                        v-model="form.instagram"
                                        class="w-full" />
                                </div>

                                <div class="field col-12 md:col-6">
                                    <label for="facebook" class="font-semibold">{{ $t('Site facebook') }}</label>
                                    <InputText
                                        id="facebook"
                                        v-model="form.facebook"
                                        class="w-full" />
                                </div>
                            </div>

                            <div class="flex gap-2 mt-4">
                                <Button
                                    type="submit"
                                    :label="$t('Save')"
                                    icon="pi pi-check"
                                    :loading="form.processing" />

                                <Button
                                    :label="$t('Cancel')"
                                    icon="pi pi-times"
                                    severity="secondary"
                                    @click="$inertia.visit(route('sites.index'))" />
                            </div>

                            <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                {{ $t('Site saved successfully.') }}
                            </Message>
                        </form>
                    </template>
                </Card>
            </div>

            <div class="col-12 lg:col-4">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-info-circle"></i>
                            <span>{{ $t('Information') }}</span>
                        </div>
                    </template>
                    <template #content>
                        <p class="m-0">
                            {{ $t('Create a new site by filling out the form. Required fields are marked with an asterisk.') }}
                        </p>
                        <div class="mt-3">
                            <small class="text-gray-600">
                                <strong>{{ $t('Required fields:') }}</strong>
                                <ul class="mt-2 pl-4">
                                    <li>{{ $t('Website URL') }}</li>
                                </ul>
                            </small>
                        </div>
                        <div class="mt-3">
                            <small class="text-gray-600">
                                <strong>{{ $t('Tips:') }}</strong>
                                <ul class="mt-2 pl-4">
                                    <li>{{ $t('Include http:// or https:// in the URL') }}</li>
                                    <li>{{ $t('Fill out as many fields as possible for better visibility') }}</li>
                                </ul>
                            </small>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}
</style>